import React, { useMemo } from "react";
import { Drawer, Box } from "@mui/material";
import { LG } from "../../styles/liquidGlass";
import { DrawerHeader } from "./ShoppingCartDrawerHeader";
import { EmptyCart } from "./ShoppingCartEmptyCart";
import { ShoppingCartItem } from "./ShoppingCartItem";
import { useCart } from "@ecommerce/store";
import { useTiendaInfo } from "../../contexts/TiendaInfoContext";
import { buildCartWhatsAppUrl } from "../../utils/whatsapp";
import { Icon } from "../liquid/LiquidPrimitives";
import { AppConfigFields } from "../../types/appConfigEntities";

const minimumPurchase = window.APP_CONFIG?.[AppConfigFields.MinimumPurchaseQuantity] || 1;

interface ShoppingCartDrawerProps {
  cartItems: any[];
  isOpen: boolean;
  onClose: () => void;
  cartCount: number;
}

const ShoppingCartDrawer: React.FC<ShoppingCartDrawerProps> = ({
  cartItems,
  isOpen,
  onClose,
}) => {
  const { updateQuantity, removeFromCart } = useCart();
  const tiendaInfo = useTiendaInfo();

  const subtotal = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [cartItems]
  );

  const whatsappUrl = useMemo(
    () =>
      buildCartWhatsAppUrl(
        cartItems.map((item) => ({
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          attributes: item.attributes,
        })),
        tiendaInfo?.whatsappNumero
      ),
    [cartItems, tiendaInfo?.whatsappNumero]
  );

  const handleUpdateQuantity = (idProduct: string, idProductVariant: string, newQuantity: number) => {
    updateQuantity(idProduct, idProductVariant, Math.max(minimumPurchase, newQuantity));
  };

  const handleRemoveItem = (idProduct: string, idProductVariant: string) => {
    removeFromCart(idProduct, idProductVariant);
  };

  const hasItems = cartItems.length > 0;

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        className: "lg-strong",
        style: {
          boxShadow: "-30px 0 80px -20px rgba(60,40,20,0.25), inset 0 1px 0 rgba(255,255,255,0.85)",
        },
      }}
      ModalProps={{
        BackdropProps: {
          sx: {
            backgroundColor: "rgba(20,16,12,0.18)",
            backdropFilter: "blur(2px)",
          },
        },
      }}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "calc(100% - 16px)", md: 444 },
          maxWidth: "calc(100% - 16px)",
          top: "12px",
          bottom: "12px",
          right: "12px",
          height: "auto",
          boxSizing: "border-box",
          color: LG.color.text,
          borderRadius: "28px !important",
          display: "flex",
          flexDirection: "column",
          px: { xs: 2, sm: 3.5 },
          py: 3,
          background: "var(--glass-bg-strong) !important",
        },
        zIndex: (theme) => theme.zIndex.drawer + 2000,
      }}
    >
      <Box sx={{ display: "contents" }}>
        <DrawerHeader onClose={onClose} />

        <Box sx={{ flexGrow: 1, overflow: "auto", height: "100%", mb: 2 }}>
          {!hasItems ? (
            <EmptyCart />
          ) : (
            <Box sx={{ mb: 2 }}>
              {cartItems.map((item, index) => (
                <ShoppingCartItem
                  key={`${item.idProduct}-${item.idProductVariant}-${index}`}
                  item={item}
                  onUpdateQuantity={handleUpdateQuantity}
                  onRemoveItem={handleRemoveItem}
                />
              ))}
            </Box>
          )}
        </Box>

        {hasItems && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 4px" }}>
              <span style={{ fontSize: 13, color: LG.color.textSecondary }}>Subtotal estimado</span>
              <span className="mono" style={{ fontSize: 16, fontWeight: 600, color: LG.color.text }}>
                ${subtotal.toLocaleString("es-AR")}
              </span>
            </div>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noreferrer"
              className="btn-primary"
              style={{ justifyContent: "center", gap: 10 }}
            >
              <Icon.bag /> Consultar pedido por WhatsApp
            </a>
            <button type="button" className="btn-ghost" style={{ justifyContent: "center" }} onClick={onClose}>
              Seguir comprando
            </button>
          </div>
        )}
      </Box>
    </Drawer>
  );
};

export default ShoppingCartDrawer;
